import type { AwardCheck } from '@myrewards/contracts';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useId, useState, type ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { useNavigate, useParams } from 'react-router';
import { meQuery } from '../../api';
import {
  checkQuery,
  completeCheck,
  documentsQuery,
  releaseClaim,
  returnCheck,
} from '../../api/ops';
import { STAGE } from '../../home/labels';
import { Hero, PageBody, TwoColumns } from '../../shell';
import {
  Button,
  ButtonLink,
  Card,
  ConfirmDialog,
  cx,
  formatDate,
  Icon,
  StatusPill,
  useToast,
} from '../../ui';
import { problemWords, QUEUE_VIEW, TYPE_LABEL } from '../labels';
import { ClaimButton, useClaimChanged } from '../queue/ClaimButton';
import { lettersHref } from '../queue/presenter';
import { BackLink, Notice, PersonChip } from '../shared';
import { CodingCard, LinesCard, ProofCard, TaxCard } from './CheckCards';
import {
  checkHeroSteps,
  checkProgress,
  checkMode,
  checklistRow,
  notYetClaimed,
  checkSubject,
  approvalWords,
  letterWords,
  type CheckMode,
} from './presenter';
import s from '../Ops.module.css';
import { ManageTabs } from '../../manage/ManageTabs';

function ClaimPanel({ check, mode }: { check: AwardCheck; mode: CheckMode }): ReactElement {
  const toast = useToast();
  const navigate = useNavigate();
  const changed = useClaimChanged();
  const [releasing, setReleasing] = useState(false);
  const [sendingBack, setSendingBack] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = check.award.id;
  const displayId = check.award.displayId ?? 'This award';

  const complete = useMutation({
    mutationFn: () => completeCheck(id, { version: check.award.version }),
    onSuccess: async () => {
      await changed(id);
      toast.show({
        tone: 'done',
        title: `Marked checked · ${displayId}`,
        body: 'It’s ready for its letters, still with you.',
      });
      void navigate(lettersHref(id));
    },
    onError: async (e) => {
      setError(problemWords(e));
      await changed(id);
    },
  });

  const release = useMutation({
    mutationFn: (reason: string) => releaseClaim(id, reason === '' ? undefined : reason),
    onSuccess: async () => {
      setError(null);
      await changed(id);
      toast.show({
        tone: 'done',
        title: mode === 'held' ? `Claim released · ${displayId}` : `Handed back · ${displayId}`,
        body: 'It stays where it is, ready for anyone on the team to take over.',
      });
    },
    onError: async (e) => {
      setError(problemWords(e));
      await changed(id);
    },
  });

  const requester = check.award.createdBy.name;
  const markNoteId = useId();
  const sendBackNoteId = useId();
  const sendBack = useMutation({
    mutationFn: (note: string) => returnCheck(id, { version: check.award.version, note }),
    onSuccess: async () => {
      setError(null);
      await changed(id);
      toast.show({
        tone: 'done',
        title: `Sent back to ${requester}`,
        body: `${displayId} is their draft again, with your note.`,
      });
      void navigate(QUEUE_VIEW.queue.href);
    },
    onError: async (e) => {
      setError(problemWords(e));
      await changed(id);
    },
  });

  return (
    <div className={s.claimPanel}>
      {mode === 'yours' && (
        <>
          {/* APP-105: one action, no tick boxes. The press is the confirmation. */}
          <div className={s.buttonWithNote}>
            <Button
              variant="cyan"
              block
              aria-describedby={markNoteId}
              isDisabled={!check.canComplete}
              isPending={complete.isPending}
              onPress={() => {
                setError(null);
                complete.mutate();
              }}
            >
              Mark checked — go to letters
            </Button>
            <p id={markNoteId} className={s.muted}>
              {check.canComplete
                ? 'You’re confirming the people, amounts, share price and charge codes above.'
                : 'Finish the items marked “To do” first.'}
            </p>
          </div>
          {/* QA-037: the action reads the same on every award; who it goes to is the line
              under it, which can wrap however long the name (APP-067 §10). */}
          {check.actions.sendBack && (
            <div className={s.buttonWithNote}>
              <Button
                variant="ghost"
                block
                aria-describedby={sendBackNoteId}
                onPress={() => {
                  setSendingBack(true);
                }}
              >
                Send back with a note
              </Button>
              <p id={sendBackNoteId} className={s.muted}>
                Goes back to {requester}, who entered it.
              </p>
            </div>
          )}
          {check.actions.handBack && (
            <Button
              variant="ghost"
              block
              onPress={() => {
                setReleasing(true);
              }}
            >
              Hand it back to the team
            </Button>
          )}
        </>
      )}
      {mode === 'yours_done' && (
        <>
          <Notice tone="done">Marked checked. It’s with you for its letters.</Notice>
          <ButtonLink href={lettersHref(id)} variant="cyan" block>
            Go to letters
          </ButtonLink>
          {check.actions.handBack && (
            <Button
              variant="ghost"
              block
              onPress={() => {
                setReleasing(true);
              }}
            >
              Hand it back to the team
            </Button>
          )}
        </>
      )}
      {mode === 'held' && check.claimedBy !== null && (
        <>
          <PersonChip
            initials={check.claimedBy.initials}
            name={`${check.claimedBy.name} is on it`}
            detail="Only the person on an award can change its check."
          />
          {check.actions.releaseOthers && (
            <Button
              variant="ghost"
              block
              onPress={() => {
                setReleasing(true);
              }}
            >
              Release this claim
            </Button>
          )}
        </>
      )}
      {mode === 'open' &&
        (check.actions.claim ? (
          <>
            <p className={s.cardText}>
              Nobody is on this award. Claim it to do its check here; anything already entered
              stays.
            </p>
            <ClaimButton
              awardId={id}
              displayId={displayId}
              version={check.award.version}
              label="Claim this award"
              variant="cyan"
              size="md"
            />
          </>
        ) : (
          <p className={s.cardText}>
            This award isn’t with the Stock Admin team right now
            {check.award.stage ? `: it’s at ${STAGE[check.award.stage].label.toLowerCase()}` : ''}.
          </p>
        ))}
      {error !== null && <Notice tone="problem">{error}</Notice>}
      <ConfirmDialog
        isOpen={sendingBack}
        onOpenChange={setSendingBack}
        title={`Send ${displayId} back to ${requester}?`}
        intro={`It becomes ${requester}’s draft again, with your note, and goes through approval again when it’s sent. Its budget hold and your claim end, and the tax estimates and OH or G&A switches entered here are dropped.`}
        summary={[
          { label: 'Award', value: displayId },
          { label: 'Goes back to', value: requester },
        ]}
        reasonLabel={`Note to ${requester}`}
        confirmLabel="Send it back"
        onConfirm={async (note) => {
          await sendBack.mutateAsync(note).catch(() => undefined);
        }}
      />
      <ConfirmDialog
        isOpen={releasing}
        onOpenChange={setReleasing}
        title={mode === 'held' ? 'Release this claim?' : 'Hand this award back?'}
        intro={
          mode === 'held'
            ? 'The award stays at its step with nobody on it, for anyone on the team to take over. The person on it now loses the claim.'
            : 'It stays at its step with nobody on it, and anything you entered stays. Anyone on the team can take it over.'
        }
        summary={[{ label: 'Award', value: displayId }]}
        reasonLabel={mode === 'held' ? 'Why are you releasing it?' : 'Note for the team (optional)'}
        reasonRequired={mode === 'held'}
        confirmLabel={mode === 'held' ? 'Release the claim' : 'Hand it back'}
        onConfirm={async (reason) => {
          await release.mutateAsync(reason).catch(() => undefined);
        }}
      />
    </div>
  );
}

function ChecklistCard({ check, mode }: { check: AwardCheck; mode: CheckMode }): ReactElement {
  const notStarted = notYetClaimed(check, mode);
  return (
    <Card
      title="Checklist"
      accent="cyan"
      help={{
        shows:
          'Everything the one check covers before an award’s letter: lines, FMV, coding, proof and, for a PAA with cash, the estimated tax per choice.',
        canDo:
          'Work down the list. When nothing is left to do, mark it checked; the award goes to letters. From the queue you can mark several ready awards checked at once.',
      }}
    >
      {notStarted && (
        <p className={s.muted}>
          Every award gets this one check before its letter. Here is what it covers; nothing shows
          as done until someone claims it.
        </p>
      )}
      <ol className={s.checklist}>
        {check.checklist.map((item, index) => {
          const row = checklistRow(item, check);
          return (
            <li key={item.id} className={s.checkItem}>
              {notStarted ? (
                <span className={cx(s.checkMark, s.checkNumber)} aria-hidden="true">
                  {index + 1}
                </span>
              ) : (
                <span
                  className={cx(
                    s.checkMark,
                    item.status === 'done' && s.checkDone,
                    item.status === 'open' && s.checkOpen,
                  )}
                  aria-hidden="true"
                >
                  <Icon
                    name={item.status === 'open' ? 'clock' : 'check'}
                    size={14}
                    strokeWidth={3}
                  />
                </span>
              )}
              <span className={s.checkText}>
                <span className={s.strong}>{row.label}</span>
                <span className={s.sub}>{row.detail}</span>
              </span>
              {!notStarted && (
                <StatusPill tone={row.status.tone} size="sm" label={row.status.label} />
              )}
            </li>
          );
        })}
      </ol>
      <ClaimPanel check={check} mode={mode} />
    </Card>
  );
}

function AboutCard({ check }: { check: AwardCheck }): ReactElement {
  const { award } = check;
  const rows: [string, string][] = [
    ['Award date', award.awardDate === null ? 'Not set yet' : formatDate(award.awardDate)],
    ['Sector', award.sector?.name ?? 'Executive'],
    ['Program', award.program?.name ?? 'No program'],
    [
      'Entered by',
      award.onBehalfOf
        ? `${award.createdBy.name} for ${award.onBehalfOf.name}`
        : award.createdBy.name,
    ],
    ['Sent', award.submittedAt === null ? 'Not sent yet' : formatDate(award.submittedAt)],
    ['Approved', approvalWords(check)],
    ['Letter goes to', letterWords(check)],
  ];
  return (
    <Card
      title="About this award"
      tone="dark"
      compact
      titleSize="sm"
      help={{
        shows: 'The award’s dates, sector, program and who entered it.',
        canDo: 'Open the award record for everything else, including its full history.',
      }}
    >
      <dl className={s.about}>
        {rows.map(([label, value]) => (
          <div key={label} className={s.aboutRow}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <Link href={`/awards/${award.id}`} className={s.onDarkLink}>
        Award record and history
      </Link>
    </Card>
  );
}

/** SA2: the one check, by the Stock Admin who holds the claim; read-only for everyone else. */
export function CheckPage(): ReactElement {
  const { id = '' } = useParams();
  const { data: me } = useSuspenseQuery(meQuery);
  const { data: check } = useSuspenseQuery(checkQuery(id));
  const { data: documents } = useQuery({
    ...documentsQuery(id),
    // While a file waits for its virus check, look again every few seconds.
    refetchInterval: (query) =>
      query.state.data?.documents.some((d) => d.scanStatus === 'pending') === true ? 5_000 : false,
  });
  const mode = checkMode(check, me.person.id);
  const canChange = mode === 'yours';
  const docs = documents?.documents ?? [];
  const displayId = check.award.displayId ?? 'This award';
  const progress = checkProgress(check, mode);
  const by = check.award.onBehalfOf
    ? `${check.award.createdBy.name} for ${check.award.onBehalfOf.name}`
    : check.award.createdBy.name;

  return (
    <>
      <Hero
        eyebrow={`${displayId} · ${TYPE_LABEL[check.award.type]}`}
        title={`The check · ${checkSubject(check)}`}
        sub={[
          check.award.sector?.name ?? 'Executive',
          `entered by ${by}`,
          check.award.submittedAt === null ? null : `sent ${formatDate(check.award.submittedAt)}`,
        ]
          .filter((part): part is string => part !== null)
          .join(' · ')}
        steps={checkHeroSteps(check, mode, progress)}
        titleTestId="check-title"
      />
      <ManageTabs current="queue" />
      <PageBody>
        <div className={s.stack}>
          <BackLink href={QUEUE_VIEW.queue.href}>Queue</BackLink>
          {mode === 'held' && check.claimedBy !== null && (
            <Notice tone="info">
              {check.claimedBy.name} is on this award, so you see its check read-only.
            </Notice>
          )}
          <TwoColumns
            sideLabel="Checklist and award details"
            main={
              <>
                <LinesCard check={check} canChange={canChange} />
                <CodingCard check={check} canChange={canChange} />
                <ProofCard check={check} documents={docs} canChange={canChange} />
                <TaxCard check={check} documents={docs} canChange={canChange} />
              </>
            }
            side={
              <>
                <ChecklistCard check={check} mode={mode} />
                <AboutCard check={check} />
              </>
            }
          />
        </div>
      </PageBody>
    </>
  );
}
